import React, {
  Fragment, useCallback,
  useEffect, useMemo,
  useReducer, useRef,
} from "react";
import { useDebouncedCallback } from "use-debounce";
import { Link } from "react-router-dom";
import StickyHeaderDataTable from "../shared/sticky-header-data-table";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import moment from "moment-timezone";
import { formatTripDateRange } from "../../helpers/dates";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import JSONPretty from "react-json-pretty";

import axios from "axios";
import ai from "../../helpers/axios";
import PageHeaderRowSearch from "../shared/page-header-row-search";
import Loader from "../loader";
import Search from "../search";
import currencyFormatter from "../../helpers/currencyFormatter";
import capitalizeFirstLetter from "../../helpers/capitalize-first-letter";
import shortenName from "../../helpers/shorten-name";
import { LoadingButton } from "../shared/loading-button";
import PaymentLinkingColumn from "./payment-linking-column";
import PaymentUnlinkModal from "./payment-unlink-modal";
import PaymentLinkModal from "./payment-link-modal";
import PaymentLinkLedgerModal from "./payment-link-ledger-modal";
import PaymentUnlinkLedgerModal from "./payment-unlink-ledger-modal";
import LinkChargeToTripModal from "./link-charge-to-trip-modal";
import CompChargeModal from "./comp-charge-modal";
import {
  formatOperatorOption,
  formatProspectOption,
  formatTripOption,
  formatContractOption,
  formatOFROption,
} from "./helpers";
import { PAYMENT_TYPE_OPTIONS } from "../../helpers/format-payment-type";
import { sleep } from "../../helpers/sleep";
import Select from "react-select";

const columns = [
  {
    name: "Post Date",
    selector: "transactionDate",
    sortable: true,
    width: "80px",
    cell: (row) => {
      if (row._isLineItem) {
        return null;
      }

      return row.transactionDate ? moment(row.transactionDate).format("M/D/YY") : "N/A";
    },
  },
  {
    name: "Contact Name",
    selector: "contactName",
    sortable: true,
    minWidth: "200px",
    cell: (row) => {
      if (row._isLineItem) {
        return null;
      }

      return (
        <Tooltip title={row.contactName || ""}>
          <div className="two-line-content">
            {row.contactName || "N/A"}
          </div>
        </Tooltip>
      );
    },
  },
  {
    name: "Reference",
    selector: "reference",
    sortable: true,
    minWidth: "250px",
    cell: (row) => {
      return (
        <Tooltip title={row.reference || ""}>
          <div className="two-line-content">
            {row.reference || "N/A"}
          </div>
        </Tooltip>
      );
    },
  },
  {
    name: "Status",
    selector: "status",
    sortable: true,
    width: "115px",
    cell: (row) => {
      return row.status;
    },
  },
  {
    name: "Reconciled",
    selector: "isReconciled",
    sortable: true,
    width: "100px",
    cell: (row) => {
      if (row._isLineItem) {
        return null;
      }

      return row.isReconciled ? "Yes" : "No";
    },
  },
  {
    name: "Amount",
    selector: "amount",
    sortable: true,
    cell: (row) => {
      return currencyFormatter.format(row.amount || 0);
    },
    width: "140px",
  },
  {
    name: "Trip #",
    selector: "trip.tripTag",
    sortable: true,
    cell: (row) => {
      if (row._isMultiLineItemParent) {
        return null;
      }

      if (row.fundsLedgerId) {
        return <Link to="/funds-ledger" target="_blank" rel="noopener noreferrer">Funds Ledger</Link>;
      }

      return row.tripId ? (
        <Link className="ellipsis-cell" to={`/trips/${row.tripId}`} target="_blank" rel="noopener noreferrer">
          <Tooltip title={row.trip?.tripTag} arrow>
            {row.trip?.tripTag}
          </Tooltip>
        </Link>
      ) : "-";
    },
    width: "100px",
  },
  {
    name: "Client / Operator",
    selector: "clientOrOperator",
    sortable: true,
    wrap: true,
    minWidth: "120px",
    maxWidth: "220px",
    cell: (row) => {
      if (row._isMultiLineItemParent || row._isLineItem) {
        return null;
      }

      if (!row.tripId && !row.fundsLedgerId) {
        return "-";
      }

      return (
        <Tooltip title={row.clientOrOperator || ""}>
          <div className="two-line-content">
            {row.clientOrOperator}
          </div>
        </Tooltip>
      );
    },
  },
  {
    name: "Trip Dates",
    selector: "firstDepartureDate",
    sortable: true,
    cell: (row) => {
      if (row._isMultiLineItemParent || row._isLineItem) {
        return null;
      }

      if (!row.firstDepartureDate) {
        return "-";
      }

      return formatTripDateRange(row.firstDepartureDate, row.lastArrivalDate);
    },
    width: "155px",
  },
  {
    name: "Routing",
    selector: "routing",
    sortable: true,
    width: "200px",
    cell: (row) => {
      if (row._isMultiLineItemParent || row._isLineItem) {
        return null;
      }

      return row.routing || null;
    },
  },
];

const STRIPE_BALANCE_TRANSACTION_TYPE_LABELS = {
  charge: "Charge",
  refund: "Refund",
  payment: "Payment",
  adjustment: "Adjustment",
};

const stripeColumns = [
  {
    name: "Post Date",
    selector: "chargedAt",
    sortable: true,
    width: "90px",
    cell: (row) => row.chargedAt ? moment(row.chargedAt).format("M/D/YY") : "N/A",
  },
  {
    name: "Card Holder",
    selector: "cardholderName",
    sortable: true,
    minWidth: "160px",
    cell: (row) => (
      <Tooltip title={row.cardholderName || ""}>
        <div className="two-line-content">{row.cardholderName || "N/A"}</div>
      </Tooltip>
    ),
  },
  {
    name: "Card",
    selector: "last4",
    sortable: true,
    width: "90px",
    cell: (row) => row.last4 ? `x${row.last4}` : "N/A",
  },
  {
    name: "Charged",
    selector: "grossAmount",
    sortable: true,
    width: "120px",
    cell: (row) => currencyFormatter.format(row.grossAmount || 0),
  },
  {
    name: "Net to Evo",
    selector: "netAmount",
    sortable: true,
    width: "120px",
    cell: (row) => currencyFormatter.format(row.netAmount || 0),
  },
  {
    name: "Stripe Fee",
    selector: "feeAmount",
    sortable: true,
    width: "110px",
    cell: (row) => currencyFormatter.format(row.feeAmount || 0),
  },
  {
    name: "Type",
    selector: "balanceTransactionType",
    sortable: true,
    width: "100px",
    cell: (row) => row.balanceTransactionType ?
      STRIPE_BALANCE_TRANSACTION_TYPE_LABELS[row.balanceTransactionType] || capitalizeFirstLetter(row.balanceTransactionType) :
      "-",
  },
  {
    name: "Category",
    selector: "categoryLabel",
    sortable: true,
    width: "130px",
    cell: (row) => row.categoryLabel || "-",
  },
  {
    name: "Trip #",
    selector: "trip.tripTag",
    sortable: true,
    width: "100px",
    cell: (row) => row.tripId ? (
      <Link className="ellipsis-cell" to={`/trips/${row.tripId}`} target="_blank" rel="noopener noreferrer">
        <Tooltip title={row.trip?.tripTag} arrow>
          {row.trip?.tripTag}
        </Tooltip>
      </Link>
    ) : "-",
  },
  {
    name: "Client / Operator",
    selector: "clientOrOperator",
    sortable: true,
    wrap: true,
    minWidth: "140px",
    maxWidth: "220px",
    cell: (row) => row.tripId ? (
      <Tooltip title={row.clientOrOperator || ""}>
        <div className="two-line-content">{row.clientOrOperator}</div>
      </Tooltip>
    ) : "-",
  },
  {
    name: "Trip Dates",
    selector: "firstDepartureDate",
    sortable: true,
    width: "155px",
    cell: (row) => {
      if (!row.firstDepartureDate) {
        return "-";
      }

      return formatTripDateRange(row.firstDepartureDate, row.lastArrivalDate);
    },
  },
  {
    name: "Routing",
    selector: "routing",
    sortable: true,
    width: "200px",
    cell: (row) => row.routing || "-",
  },
];

const AMEX_NEEDS_ACTION_COLOR = "#ffe0a3";

const amexColumnsBeforeAction = [
  {
    name: "Post Date",
    selector: "transactionDate",
    sortable: true,
    width: "90px",
    cell: (row) => {
      if (row._isMultiLineItemParent) {
        return null;
      }

      return row.transactionDate ? moment(row.transactionDate).format("M/D/YY") : "N/A";
    },
  },
  {
    name: "Card",
    selector: "cardName",
    sortable: true,
    width: "110px",
    cell: (row) => row.cardName || "-",
  },
  {
    name: "Contact Name",
    selector: "contactName",
    sortable: true,
    minWidth: "180px",
    cell: (row) => {
      if (row._isLineItem) {
        return null;
      }

      return (
        <Tooltip title={row.contactName || ""}>
          <div className="two-line-content">
            {row.contactName || "N/A"}
          </div>
        </Tooltip>
      );
    },
  },
  {
    name: "Reference",
    selector: "reference",
    sortable: true,
    minWidth: "200px",
    cell: (row) => (
      <Tooltip title={row.reference || ""}>
        <div className="two-line-content">
          {row.reference || "N/A"}
        </div>
      </Tooltip>
    ),
  },
  {
    name: "Category",
    selector: "categoryLabel",
    sortable: true,
    width: "130px",
    cell: (row) => {
      if (row._isMultiLineItemParent) {
        return null;
      }

      return row.categoryLabel || "-";
    },
  },
  {
    name: "Amount",
    selector: "amount",
    sortable: true,
    width: "130px",
    cell: (row) => currencyFormatter.format(row.amount || 0),
  },
];

const amexColumnsAfterAction = [
  {
    name: "Trip #",
    selector: "trip.tripTag",
    sortable: true,
    width: "100px",
    cell: (row) => {
      if (row._isMultiLineItemParent) {
        return null;
      }

      return row.tripId ? (
        <Link className="ellipsis-cell" to={`/trips/${row.tripId}`} target="_blank" rel="noopener noreferrer">
          <Tooltip title={row.trip?.tripTag} arrow>
            {row.trip?.tripTag}
          </Tooltip>
        </Link>
      ) : "-";
    },
  },
  {
    name: "Client",
    selector: "clientOrOperator",
    sortable: true,
    wrap: true,
    minWidth: "140px",
    maxWidth: "220px",
    cell: (row) => {
      if (row._isMultiLineItemParent) {
        return null;
      }

      if (!row.tripId) {
        return "-";
      }

      return (
        <Tooltip title={row.clientOrOperator || ""}>
          <div className="two-line-content">
            {row.clientOrOperator}
          </div>
        </Tooltip>
      );
    },
  },
  {
    name: "Trip Dates",
    selector: "firstDepartureDate",
    sortable: true,
    width: "155px",
    cell: (row) => {
      if (row._isMultiLineItemParent || !row.firstDepartureDate) {
        return row._isMultiLineItemParent ? null : "-";
      }

      return formatTripDateRange(row.firstDepartureDate, row.lastArrivalDate);
    },
  },
  {
    name: "Owner",
    selector: "trip.owner.name",
    sortable: true,
    width: "120px",
    cell: (row) => {
      if (row._isMultiLineItemParent) {
        return null;
      }

      return row.trip?.owner?.name ? shortenName(row.trip.owner.name) : "-";
    },
  },
  {
    name: "Routing",
    selector: "routing",
    sortable: true,
    width: "180px",
    cell: (row) => {
      if (row._isMultiLineItemParent) {
        return null;
      }

      return row.routing || "-";
    },
  },
];

const conditionalRowStyles = [
  {
    when: (row) => !!row.tripId && row.categoryCode !== "501" && !row._isMultiLineItemParent && !row._isLineItem,
    style: {
      backgroundColor: "#9ce19b !important",
      "&:hover": {
        cursor: "pointer",
        backgroundColor: "#247523 !important",
      },
    },
  },
  {
    when: (row) => row._isLineItem && !!row.tripId && row.categoryCode !== "501",
    style: {
      backgroundColor: "#9ce19b !important",
      "&:hover": {
        cursor: "pointer",
        backgroundColor: "#247523 !important",
      },
      "> div:first-of-type > button": {
        display: "none !important",
      },
    },
  },
  {
    when: (row) => !row._isMultiLineItemParent && !!row.categoryCode && row.categoryCode !== "501" && !!row.incidentalChargeId && !row.actionType,
    style: {
      backgroundColor: `${AMEX_NEEDS_ACTION_COLOR} !important`,
      "&:hover": {
        cursor: "pointer",
        backgroundColor: "#F6D27A !important",
      },
    },
  },
  {
    when: (row) => row._isMultiLineItemParent,
    style: {
      backgroundColor: "lightgray !important",
      "&:hover": {
        cursor: "default",
        backgroundColor: "#eeeeee !important",
      },
    },
  },
  {
    when: (row) => row._isLineItem && !row.tripId,
    style: {
      backgroundColor: "#eaeaea !important",
      "> div:first-of-type > button": {
        display: "none !important",
      },
    },
  },
  {
    when: (row) => row.pending && !row.actionType,
    style: {
      backgroundColor: "#f6d992 !important",
      "&:hover": {
        cursor: "pointer",
        backgroundColor: "#F6CF92 !important",
      },
    },
  },
  {
    when: (row) => row.disabled || row.status === "DELETED",
    style: {
      backgroundColor: "#ffcdd2 !important",
      "&:hover": {
        cursor: "pointer",
        backgroundColor: "#ef9a9a !important",
      },
    },
  },
  {
    when: (row) => !!row.chargeId && row.status !== "succeeded",
    style: {
      backgroundColor: "#f6d992 !important",
      "&:hover": {
        cursor: "pointer",
        backgroundColor: "#F6CF92 !important",
      },
    },
  },
  {
    when: (row) => !!row.chargeId && row.refunded,
    style: {
      backgroundColor: "#ffcdd2 !important",
      "&:hover": {
        cursor: "pointer",
        backgroundColor: "#ef9a9a !important",
      },
    },
  },
];

const REFRESH_TIMEOUT = 2 * 60 * 1000;

const toastOptions = {
  position: "top-right",
  autoClose: 10000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  icon: false,
};

const showToastMessage = (id, message, options) => {
  if (id) {
    toast.update(id, {
      render: message,
      ...toastOptions,
      ...options,
    });
  } else {
    return toast(message, {
      ...toastOptions,
      ...options,
    });
  }
};

const AMEX_CATEGORY_LABELS = {
  "501": "Operator",
  "503": "Catering",
  "504": "Transportation",
};

const STRIPE_TRANSFER_DESCRIPTION_REGEX = /^stripe transfer/i;
const isStripeTransferLineItem = (paymentToProcess) => {
  const description = paymentToProcess._isLineItem
    ? paymentToProcess.reference
    : paymentToProcess.item?.lineItems?.[0]?.description;

  return STRIPE_TRANSFER_DESCRIPTION_REGEX.test((description || "").trim());
};

const expandLineItems = (transactions) =>
  transactions.flatMap((tx) => {
    const lineItems = tx.item?.lineItems ?? [];
    // lineItemCount: the server's count before it hid operator lines, so a
    // multi-line charge still links line by line.
    if (lineItems.length <= 1 && !(tx.lineItemCount > 1)) return [tx];

    const parentRow = { ...tx, _isMultiLineItemParent: true };
    const childRows = lineItems.map((li) => {
      const linkData = tx.lineItemLinks?.[li.lineItemID] ?? null;
      return {
        _isLineItem: true,
        _id: tx._id,
        _lineItemID: li.lineItemID,
        targetingPayments: tx.targetingPayments,
        contactName: tx.contactName,
        transactionDate: tx.transactionDate,
        cardName: tx.cardName,
        reference: li.description?.trim() || "",
        status: li.accountCode || "",
        categoryCode: li.accountCode || null,
        categoryLabel: AMEX_CATEGORY_LABELS[li.accountCode] || li.accountCode || null,
        amount: li.lineAmount,
        tripId: linkData?.tripId ?? null,
        incidentalChargeId: linkData?.incidentalChargeId ?? null,
        actionType: linkData?.actionType ?? null,
        amountToCharge: linkData?.amountToCharge ?? null,
        amountToComp: linkData?.amountToComp ?? null,
        clientPaymentHistoryId: linkData?.clientPaymentHistoryId ?? null,
        operatorPaymentHistoryId: linkData?.operatorPaymentHistoryId ?? null,
        trip: linkData?.tripId
          ? {
            _id: linkData.tripId,
            tripTag: linkData.tripTag,
            owner: linkData.owner ?? null,
          }
          : null,
      };
    });

    return [parentRow, ...childRows];
  });

const reducer = (state, action) => {
  switch (action.type) {
    case "tableDataBeganDownloading": {
      return {
        ...state,
        isTableLoading: action.payload.loadingStatus,
        selectedRowsIDs: [],
      };
    }

    case "tableDataDownloaded": {
      return {
        ...state,
        isTableLoading: false,
        data: expandLineItems(action?.payload?.data?.data || []),
        totalLength: action?.payload?.data?.countTotal,
      };
    }

    case "summaryDataDownloaded": {
      return {
        ...state,
        isSummaryLoading: false,
        summary: action?.payload?.data || {},
      };
    }

    case "search": {
      return {
        ...state,
        query: action.query,
        currentPage: 1,
      };
    }

    case "pageChanged": {
      return {
        ...state,
        currentPage: action.payload.page,
      };
    }

    case "rowsPerPageChanged": {
      return {
        ...state,
        countPerPage: action.payload.currentRowsPerPage,
        currentPage: 1,
      };
    }

    case "tableSort": {
      return {
        ...state,
        sortBy: action.payload.sortBy,
        sortDirection: action.payload.sortDirection,
        currentPage: 1,
      };
    }

    case "selectRows": {
      return {
        ...state,
        selectedRowsIDs: action.selectedRowsIDs,
        data: [...state.data],
      };
    }

    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    case "typeChanged": {
      return {
        ...state,
        type: action.payload.type,
        sortBy: action.payload.type === "stripe" ? "chargedAt" : "transactionDate",
        sortDirection: "desc",
        currentPage: 1,
      };
    }

    case "linkPayment": {
      const paymentToProcess = action.payload.paymentToProcess;
      const paymentRecord = paymentToProcess.paymentRecord;
      const isStripe = !!paymentToProcess.chargeId;

      return {
        ...state,
        isLinkModalOpen: true,
        paymentToProcess,
        linkModalTarget: {
          ...initialState.linkModalTarget,
          [action.payload.target]: true,
        },
        linkModalValues: {
          paymentType: isStripe ?
            PAYMENT_TYPE_OPTIONS.find(({ value }) => value === "CC") ?? null :
            paymentRecord?.paymentType ?
              PAYMENT_TYPE_OPTIONS.find(({ value }) => value === paymentRecord.paymentType) ?? null :
              isStripeTransferLineItem(paymentToProcess) ?
                PAYMENT_TYPE_OPTIONS.find(({ value }) => value === "CC") ?? null :
                !paymentToProcess._isLineItem ?
                  PAYMENT_TYPE_OPTIONS.find(({ value }) => value === "Wire") :
                  paymentToProcess.contactName === "Stripe" ?
                    PAYMENT_TYPE_OPTIONS.find(({ value }) => value === "CC") :
                    null,
          amount: isStripe ? paymentToProcess.netAmount : paymentToProcess.amount,
          transactionType: (isStripe ? paymentToProcess.netAmount : paymentToProcess.amount) >= 0 ? {
            label: "Received",
            value: "received",
          } : {
            label: "Spent",
            value: "spent",
          },
          paymentDate: isStripe ?
            paymentToProcess.chargedAt :
            paymentRecord ? paymentRecord.paymentDate : paymentToProcess.transactionDate,
          notes: isStripe ?
            [
              { label: "Cardholder", value: paymentToProcess.cardholderName },
              { label: "Reference", value: paymentToProcess.description },
            ].map(({ label, value }) => `${label}: ${value || "'-'"}`).join("; ") :
            paymentRecord ? paymentRecord.notes : (
              [
                {
                  label: "Contact Name",
                  value: paymentToProcess.contactName,
                },
                {
                  label: "Reference",
                  value: paymentToProcess.reference,
                },
              ]
            ).map(({ label, value }) => `${label}: ${value || "'-'"}`).join("; "),
          selectedOperator: paymentToProcess.operator?._id ? formatOperatorOption(paymentToProcess.operator) : null,
          selectedProspect: paymentToProcess.clientData?._id ? formatProspectOption(paymentToProcess.clientData) : null,
          selectedTrip: paymentToProcess.trip?._id ? formatTripOption(paymentToProcess.trip) : null,
          selectedContract: paymentToProcess.contract?._id ? formatContractOption(paymentToProcess.contract) : null,
          selectedOFR: paymentToProcess.ofr?._id ? formatOFROption(paymentToProcess.ofr) : null,
        },
      };
    }

    case "unlinkPayment": {
      return {
        ...state,
        isUnlinkModalOpen: true,
        paymentToProcess: action.payload.paymentToProcess,
      };
    }

    case "closeLinkProcessingModal": {
      return {
        ...state,
        isLinkModalOpen: false,
        isUnlinkModalOpen: false,
        paymentToProcess: initialState.paymentToProcess,
        linkModalTarget: initialState.linkModalTarget,
        linkModalValues: initialState.linkModalValues,
      };
    }

    case "linkModalFieldChanged": {
      return {
        ...state,
        linkModalValues: {
          ...state.linkModalValues,
          [action.field]: action.value,
        },
      };
    }

    default:
      break;
  }
  return state;
};

const FILTER_BY_DELETED_OPTIONS = [
  {
    label: "Hide Deleted",
    value: true,
  },
  {
    label: "Show Deleted",
    value: false,
  },
];

const FILTER_BY_REFUNDED_OPTIONS = [
  {
    label: "Hide Refunded",
    value: true,
  },
  {
    label: "Show Refunded",
    value: false,
  },
];

const FILTER_BY_PENDING_OPTIONS = [
  {
    label: "Hide Pending",
    value: true,
  },
  {
    label: "Show Pending",
    value: false,
  },
];

const FILTER_BY_NON_FLIGHT_OPTIONS = [
  {
    label: "Hide Non-Flight",
    value: true,
  },
  {
    label: "Show Non-Flight",
    value: false,
  },
];

const FILTER_BY_CODE_OPTIONS = [
  {
    label: "Hide Other Codes",
    value: true,
  },
  {
    label: "Show Other Codes",
    value: false,
  },
];

const FILTER_BY_STRIPE_OPTIONS = [
  {
    label: "Hide Stripe",
    value: true,
  },
  {
    label: "Show Stripe",
    value: false,
  },
];

const initialState = {
  type: "clients",
  isSummaryLoading: true,
  isTableLoading: true,
  isRefreshing: false,
  summary: {},
  data: [],
  totalLength: 0,
  query: "",
  filterDeleted: true,
  filterPending: true,
  filterNonFlight: true,
  filterStripe: true,
  countPerPage: 50,
  currentPage: 1,
  sortBy: "transactionDate",
  sortDirection: "desc",
  selectedRowsIDs: [],
  refreshDataTimestamp: Date.now(),
  isLinkModalOpen: false,
  linkModalTarget: {
    contract: false,
    ofr: false,
  },
  linkModalValues: {},
  isUnlinkModalOpen: false,
  isLinkLedgerModalOpen: false,
  isUnlinkLedgerModalOpen: false,
  isLinkTripModalOpen: false,
  isCompChargeModalOpen: false,
  compChargeModalMode: "assign",
  paymentToProcess: {},
};

const AccountingTransactionDetails = ({ data }) => {
  return (
    <div className="p-3">
      <JSONPretty data={data.item}/>
    </div>
  );
};

const AccountingDashboardContent = () => {
  const { user } = useAuth0();

  const userRoles = user && user["https://new.evojets.com/roles"];
  const isAccounting = user && user["https://new.evojets.com/appMetadata"]?.isAccounting;
  const isAccountingLedger = user && user["https://new.evojets.com/appMetadata"]?.isAccountingLedger;

  const superAdmin = userRoles.some(function (role) {
    return "Super Admin" === role;
  });

  const isAccountingAdmin = superAdmin || isAccounting;

  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  // Lazy-init so a non-accounting session never renders even a single frame with
  // state.type still at its "clients" default — that one frame was enough to briefly
  // show Contract/OFR linking options and the Hide/Show Other Codes filter (both gated
  // on `state.type !== "amex" || isAccountingAdmin`) before the effect below corrected it.
  const [state, dispatch] = useReducer(
    reducer,
    isAccountingAdmin,
    (accountingAdmin) => (accountingAdmin ? initialState : { ...initialState, type: "amex" }),
  );

  useEffect(() => {
    if (!isAccountingAdmin && state.type !== "amex") {
      dispatch({ type: "fieldChanged", field: "type", value: "amex" });
    }
  }, [isAccountingAdmin, state.type]);

  const abortControllerRef = useRef(null);

  useEffect(() => {
    document.title = "Accounting | evoJets HQ";
  }, []);

  const getSummaryData = useCallback(async (type) => {
    dispatch({ type: "fieldChanged", field: "isSummaryLoading", value: true });

    try {
      const token = await getAccessTokenSilently().catch(() => {
        loginWithRedirect();
      });

      const { data } = await ai.auth(token).get(
        ["clients", "operators", "amex"].includes(type)
          ? `/api/xero/tenant-summary`
          : type === "stripe"
            ? `/api/stripe/account-summary`
            : `/api/plaid/account-balances-summary`,
        {
          params: {
            type,
          },
        },
      );

      dispatch({
        type: "summaryDataDownloaded",
        payload: {
          data,
        },
      });
    } finally {
      dispatch({ type: "fieldChanged", field: "isSummaryLoading", value: false });
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  const getTableData = useCallback(async (params, loadSilently = false) => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    let loadingStatus = !loadSilently;

    dispatch({ type: "tableDataBeganDownloading", payload: { loadingStatus } });

    try {
      const token = await getAccessTokenSilently().catch(() => {
        loginWithRedirect();
      });

      const provider = ["clients", "operators", "amex"].includes(params.type)
        ? "xero"
        : params.type === "stripe"
          ? "stripe"
          : "plaid";

      const { data } = await ai.auth(token).get(`/api/${provider}/transactions`, {
        params,
        signal,
      });

      dispatch({
        type: "tableDataDownloaded",
        payload: {
          data,
        },
      });
      dispatch({ type: "fieldChanged", field: "isTableLoading", value: false });
    } catch (error) {
      if (axios.isCancel(error)) return;
      dispatch({ type: "fieldChanged", field: "isTableLoading", value: false });
    }
  }, [getAccessTokenSilently, loginWithRedirect]);

  const handlePageChange = (page, totalRows) => {
    dispatch({
      type: "pageChanged",
      payload: {
        page,
        totalRows,
      },
    });
  };

  const handleRowsPerPageChange = (currentRowsPerPage, currentPage) => {
    dispatch({
      type: "rowsPerPageChanged",
      payload: {
        currentRowsPerPage,
        currentPage,
      },
    });
  };

  const handleSort = (column, _) => {
    const newDirection =
      column.selector === state.sortBy
        ? state.sortDirection === "asc"
          ? "desc"
          : "asc"
        : "desc";

    dispatch({
      type: "tableSort",
      payload: {
        sortBy: column.selector,
        sortDirection: newDirection,
      },
    });
  };

  const handleRowClick = useCallback((row, event) => {
    event.preventDefault();

    dispatch({
      type: "selectRows",
      selectedRowsIDs: state.selectedRowsIDs.find((selectedRowID) => selectedRowID === row._id) ? [...state.selectedRowsIDs].filter((selectedRowID) => selectedRowID !== row._id) : [
        ...state.selectedRowsIDs,
        row._id,
      ],
    });
  }, [state.selectedRowsIDs]);

  const isSelectableRowSelected = useCallback(
    (row) => state.selectedRowsIDs.find((selectedRowID) => selectedRowID === row._id),
    [state.selectedRowsIDs],
  );

  const refreshTableData = useCallback(
    () =>
      getTableData({
        type: state.type,
        query: state.query,
        filterDeleted: state.filterDeleted,
        filterPending: state.filterPending,
        filterNonFlight: state.filterNonFlight,
        filterStripe: state.filterStripe,
        page: state.currentPage,
        countPerPage: state.countPerPage,
        sortBy: state.sortBy,
        sortDirection: state.sortDirection,
      }, true),
    [
      getTableData,
      state.type,
      state.query,
      state.filterDeleted,
      state.filterPending,
      state.filterNonFlight,
      state.filterStripe,
      state.currentPage,
      state.countPerPage,
      state.sortBy,
      state.sortDirection,
    ],
  );

  const debouncedGetTableData = useDebouncedCallback(
    (params) => {
      getTableData(params);
    },
    500,
  );

  const handleSearchInputChange = (e) => {
    let query = e.target.value.toLowerCase();
    dispatch({ type: "search", query });
  };

  const handleDeletedFilterChange = useCallback(({ value }) => {
    dispatch({ type: "fieldChanged", field: "filterDeleted", value });
  }, []);

  const handlePendingFilterChange = useCallback(({ value }) => {
    dispatch({ type: "fieldChanged", field: "filterPending", value });
  }, []);

  const handleNonFlightFilterChange = useCallback(({ value }) => {
    dispatch({ type: "fieldChanged", field: "filterNonFlight", value });
  }, []);

  const handleStripeFilterChange = useCallback(({ value }) => {
    dispatch({ type: "fieldChanged", field: "filterStripe", value });
  }, []);

  useEffect(() => {
    getSummaryData(state.type);
  }, [getSummaryData, state.type, state.refreshDataTimestamp]);

  useEffect(() => {
    debouncedGetTableData({
      type: state.type,
      query: state.query,
      filterDeleted: state.filterDeleted,
      filterPending: state.filterPending,
      filterNonFlight: state.filterNonFlight,
      filterStripe: state.filterStripe,
      page: state.currentPage,
      countPerPage: state.countPerPage,
      sortBy: state.sortBy,
      sortDirection: state.sortDirection,
    });
  }, [
    debouncedGetTableData,
    state.type,
    state.query,
    state.filterDeleted,
    state.filterPending,
    state.filterNonFlight,
    state.filterStripe,
    state.currentPage,
    state.countPerPage,
    state.sortBy,
    state.sortDirection,
    state.refreshDataTimestamp,
  ]);

  const handleOpenLinkModal = useCallback(payment => triggerName => {
    if (triggerName) {
      dispatch({
        type: "linkPayment",
        payload: {
          paymentToProcess: payment,
          target: triggerName,
        },
      });
    }
  }, []);

  const handleOpenUnlinkModal = useCallback(payment => () => {
    dispatch({
      type: "unlinkPayment",
      payload: {
        paymentToProcess: payment,
      },
    });
  }, []);

  const handleCloseLinkProcessingModal = useCallback(async (refresh = true) => {
    if (refresh) {
      await refreshTableData();
    }

    dispatch({
      type: "closeLinkProcessingModal",
    });
  }, [refreshTableData]);

  const handleOpenLinkLedgerModal = useCallback(payment => () => {
    dispatch({ type: "fieldChanged", field: "paymentToProcess", value: payment });
    dispatch({ type: "fieldChanged", field: "isLinkLedgerModalOpen", value: true });
  }, []);

  const handleOpenLinkTripModal = useCallback(payment => () => {
    dispatch({ type: "fieldChanged", field: "paymentToProcess", value: payment });
    dispatch({ type: "fieldChanged", field: "isLinkTripModalOpen", value: true });
  }, []);

  const handleCloseLinkTripModal = useCallback(async (refresh = true) => {
    if (refresh) {
      await refreshTableData();
    }

    dispatch({ type: "fieldChanged", field: "isLinkTripModalOpen", value: false });
    dispatch({ type: "fieldChanged", field: "paymentToProcess", value: {} });
  }, [refreshTableData]);

  const handleOpenUnlinkLedgerModal = useCallback(payment => () => {
    dispatch({ type: "fieldChanged", field: "paymentToProcess", value: payment });
    dispatch({ type: "fieldChanged", field: "isUnlinkLedgerModalOpen", value: true });
  }, []);

  const handleOpenAssignChargeModal = useCallback(payment => () => {
    dispatch({ type: "fieldChanged", field: "paymentToProcess", value: payment });
    dispatch({ type: "fieldChanged", field: "compChargeModalMode", value: "assign" });
    dispatch({ type: "fieldChanged", field: "isCompChargeModalOpen", value: true });
  }, []);

  const handleOpenCompChargeModal = useCallback((payment) => {
    dispatch({ type: "fieldChanged", field: "paymentToProcess", value: payment });
    dispatch({ type: "fieldChanged", field: "compChargeModalMode", value: "comp" });
    dispatch({ type: "fieldChanged", field: "isCompChargeModalOpen", value: true });
  }, []);

  const handleCloseCompChargeModal = useCallback(async (refresh = true) => {
    if (refresh) {
      await refreshTableData();
    }

    dispatch({ type: "fieldChanged", field: "isCompChargeModalOpen", value: false });
    dispatch({ type: "fieldChanged", field: "paymentToProcess", value: {} });
  }, [refreshTableData]);

  const handleCloseLedgerModal = useCallback(async (refresh = true) => {
    if (refresh) {
      await refreshTableData();
    }

    dispatch({ type: "fieldChanged", field: "isLinkLedgerModalOpen", value: false });
    dispatch({ type: "fieldChanged", field: "isUnlinkLedgerModalOpen", value: false });
    dispatch({ type: "fieldChanged", field: "paymentToProcess", value: {} });
  }, [refreshTableData]);

  const linkingColumn = useMemo(() => ({
    name: "Linking",
    width: "90px",
    cell: (row) => {
      if (row.disabled || row.pending || row._isMultiLineItemParent) {
        return null;
      }

      return (
        <PaymentLinkingColumn
          row={row}
          handleOpenLinkModal={handleOpenLinkModal(row)}
          handleOpenUnlinkModal={handleOpenUnlinkModal(row)}
          handleOpenLinkLedgerModal={handleOpenLinkLedgerModal(row)}
          handleOpenUnlinkLedgerModal={handleOpenUnlinkLedgerModal(row)}
          handleOpenLinkTripModal={handleOpenLinkTripModal(row)}
          handleOpenAssignChargeModal={handleOpenAssignChargeModal(row)}
          showFundsLedger={isAccountingLedger && !row._isLineItem}
          showContractAndOFR={state.type !== "amex" || isAccountingAdmin}
        />
      );
    },
  }), [
    handleOpenLinkModal,
    handleOpenUnlinkModal,
    handleOpenLinkLedgerModal,
    handleOpenUnlinkLedgerModal,
    handleOpenLinkTripModal,
    handleOpenAssignChargeModal,
    state.type,
    isAccountingLedger,
    isAccountingAdmin,
  ]);

  const amexActionColumn = useMemo(() => ({
    name: "Action",
    selector: "actionType",
    sortable: false,
    width: "150px",
    cell: (row) => {
      if (row._isMultiLineItemParent) {
        return null;
      }

      if (row.categoryCode === "501") {
        return "n/a";
      }

      if (row.actionType === "comp" || row.actionType === "chargeCard") {
        const label = row.actionType === "comp"
          ? `Comp - ${currencyFormatter.format(row.amountToComp || 0)}`
          : "Charge Card";

        const isTripOwner = !!row.trip?.owner?.id && row.trip.owner.id === user?.sub;

        if (!isAccountingAdmin && !isTripOwner) {
          return label;
        }

        return (
          <button
            type="button"
            className="base-button-link"
            onClick={() => handleOpenCompChargeModal(row)}
          >
            {label}
          </button>
        );
      }

      if (row.incidentalChargeId) {
        return (
          <button
            type="button"
            className="base-button-link"
            onClick={() => handleOpenCompChargeModal(row)}
          >
            Select action
          </button>
        );
      }

      return "-";
    },
  }), [handleOpenCompChargeModal, isAccountingAdmin, user?.sub]);

  const dataColumns = useMemo(
    () => {
      if (state.type === "stripe") {
        return [...stripeColumns, linkingColumn];
      }

      if (state.type === "amex") {
        return [...amexColumnsBeforeAction, amexActionColumn, ...amexColumnsAfterAction, linkingColumn];
      }

      return [...columns, linkingColumn];
    },
    [state.type, linkingColumn, amexActionColumn],
  );

  const handleChangeType = useCallback(
    (event, newType) => {
      if (!newType || newType === state.type) {
        return;
      }

      dispatch({ type: "typeChanged", payload: { type: newType } });
    }, [state.type]);

  const handleRefresh = useCallback(async () => {
    dispatch({ type: "fieldChanged", field: "isRefreshing", value: true });

    const toastId = showToastMessage(null, "Updating balances and transactions data…", {
      id: "refreshToastMessage",
      theme: "dark",
      autoClose: REFRESH_TIMEOUT + 100,
    });

    try {
      const token = await getAccessTokenSilently().catch(() => {
        loginWithRedirect();
      });

      const refreshProvider = ["clients", "operators", "amex"].includes(state.type)
        ? "xero"
        : state.type === "stripe"
          ? "stripe"
          : "plaid";

      await ai.auth(token).post(`/api/${refreshProvider}/refresh`, {});
      await sleep(REFRESH_TIMEOUT);

      dispatch({ type: "fieldChanged", field: "refreshDataTimestamp", value: Date.now() });
      showToastMessage(toastId, "Balances and transactions are updated", { type: "success" });
    } catch (error) {
      showToastMessage(toastId, "Failed to updated balances and transactions", "error");
    } finally {
      dispatch({ type: "fieldChanged", field: "isRefreshing", value: false });
    }
  }, [getAccessTokenSilently, loginWithRedirect, state.type]);

  return (
    <Fragment>
      <div className="mt-10 d-flex justify-content-between align-items-center gap-10 account-summary-mobile-block">
        <ToggleButtonGroup
          color="primary"
          value={state.type}
          exclusive
          onChange={handleChangeType}
        >
          {isAccountingAdmin && <ToggleButton value="clients">Clients</ToggleButton>}
          {isAccountingAdmin && <ToggleButton value="operators">Operators</ToggleButton>}
          <ToggleButton value="amex">AMEX</ToggleButton>
          {isAccountingAdmin && <ToggleButton value="stripe">Stripe</ToggleButton>}
        </ToggleButtonGroup>

        <div className="d-flex align-items-center gap-10 account-summary-mobile-block">
          <div className="d-flex align-items-start gap-10 position-relative p-3 account-summary-mobile-block">
            {state.isSummaryLoading && (
              <div className="position-absolute w-100 h-100 custom-loader-wrapper">
                <Loader additionalClasses="position-absolute top-50 start-50 translate-middle"/>
              </div>
            )}

            <div className="flex-grow-1">
              <div className="d-flex gap-5 align-items-center justify-content-between">
                <div><b>Available Balance:</b></div>
                <div>{state.summary?.availableBalance ? currencyFormatter.format(state.summary?.availableBalance) : "N/A"}</div>
              </div>

              <div className="d-flex gap-5 align-items-center justify-content-between">
                <div><b>Current Balance:</b></div>
                <div>{state.summary?.currentBalance ? currencyFormatter.format(state.summary?.currentBalance) : "N/A"}</div>
              </div>
            </div>

            <div className="flex-grow-1">
              <div className="d-flex gap-5 align-items-center justify-content-between">
                <div>
                  <b>
                    Ending balance
                    {state.summary?.endingBalanceSyncedAt ? moment(state.summary?.endingBalanceSyncedAt).calendar(null, {
                      sameDay: " ([today] M/D/YY)",
                      lastDay: " ([yesterday] M/D/YY)",
                      sameElse: " (M/D/YY)",
                    }) : ""}
                    :</b>
                </div>
                <div>{state.summary?.endingBalance ? currencyFormatter.format(state.summary?.endingBalance) : "N/A"}</div>
              </div>

              <div className="d-flex gap-5 align-items-center justify-content-between">
                <div>
                  <b>
                    Opening balance
                    {state.summary?.openingBalanceSyncedAt ? moment(state.summary?.openingBalanceSyncedAt).calendar(null, {
                      sameDay: " ([today] M/D/YY)",
                      lastDay: " ([yesterday] M/D/YY)",
                      sameElse: " (M/D/YY)",
                    }) : ""}
                    :</b>
                </div>
                <div>{state.summary?.openingBalance ? currencyFormatter.format(state.summary?.openingBalance) : "N/A"}</div>
              </div>
            </div>

            <div className="flex-grow-1">
              <div className="d-flex gap-5 align-items-center justify-content-between">
                <div><b>Last Updated Balance:</b></div>
                <div>{state.summary?.lastBalanceSyncedAt ? moment(state.summary?.lastBalanceSyncedAt).format("M/D/YY - h:mm a") : "N/A"}</div>
              </div>
              <div className="d-flex gap-5 align-items-center justify-content-between">
                <div><b>Last Updated Transactions:</b></div>
                <div>{state.summary?.lastTransactionsSyncedAt ? moment(state.summary?.lastTransactionsSyncedAt).format("M/D/YY - h:mm a") : "N/A"}</div>
              </div>
              <div className="d-flex gap-5 align-items-center justify-content-between">
                <div><b>Last Webhook Update:</b></div>
                <div>{state.summary?.lastWebhookSyncedAt ? moment(state.summary?.lastWebhookSyncedAt).format("M/D/YY - h:mm a") : "N/A"}</div>
              </div>
            </div>
          </div>

          <LoadingButton
            className="base-button evo-blue flex-half"
            type="button"
            isLoading={state.isRefreshing}
            onClick={handleRefresh}
          >
            Refresh
          </LoadingButton>
        </div>
      </div>

      <PageHeaderRowSearch
        h1="Accounting Dashboard"
        pretitle="Business"
        breadcrumbs={[{ link: "/accounting", label: "Accounting Dashboard" }]}
        extraClasses="page-header-trips-l1"
        rightColumn={
          <div id="trip-actions-row">
            {(state.type !== "amex" || isAccountingAdmin) && (
              <Select
                options={state.type === "amex" ? FILTER_BY_CODE_OPTIONS : FILTER_BY_NON_FLIGHT_OPTIONS}
                classNamePrefix="select"
                className="accounting-filter"
                placeholder={state.type === "amex" ? "Other Codes" : "Non-Flight"}
                value={(state.type === "amex" ? FILTER_BY_CODE_OPTIONS : FILTER_BY_NON_FLIGHT_OPTIONS)
                  .find(({ value }) => value === state.filterNonFlight)}
                onChange={handleNonFlightFilterChange}
              />
            )}

            <Select
              options={state.type === "stripe" ? FILTER_BY_REFUNDED_OPTIONS : FILTER_BY_DELETED_OPTIONS}
              classNamePrefix="select"
              className="accounting-filter"
              placeholder="Filter by Deleted"
              value={(state.type === "stripe" ? FILTER_BY_REFUNDED_OPTIONS : FILTER_BY_DELETED_OPTIONS)
                .find(({ value }) => value === state.filterDeleted)}
              onChange={handleDeletedFilterChange}
            />

            <Select
              options={FILTER_BY_PENDING_OPTIONS}
              classNamePrefix="select"
              className="accounting-filter"
              placeholder="Filter by Pending"
              value={FILTER_BY_PENDING_OPTIONS.find(({ value }) => value === state.filterPending)}
              onChange={handlePendingFilterChange}
            />

            {state.type === "clients" && (
              <Select
                options={FILTER_BY_STRIPE_OPTIONS}
                classNamePrefix="select"
                className="accounting-filter"
                placeholder="Filter by Stripe"
                value={FILTER_BY_STRIPE_OPTIONS.find(({ value }) => value === state.filterStripe)}
                onChange={handleStripeFilterChange}
              />
            )}

            <Search
              onSearch={handleSearchInputChange}
              placeholder="Search"
              query={state.query}
            />
          </div>
        }
      />

      <div className="server-pagination-table-container trips-list-table-container">
        <StickyHeaderDataTable
          columns={dataColumns}
          data={state.data}
          pagination
          paginationServer
          paginationTotalRows={state.totalLength}
          paginationPerPage={state.countPerPage}
          paginationDefaultPage={state.currentPage}
          paginationRowsPerPageOptions={[10, 25, 50, 100, 150, 200, 250, 500]}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleRowsPerPageChange}
          sortServer
          onSort={handleSort}
          defaultSortFieldId={state.sortBy}
          sortDirection={state.sortDirection}
          progressPending={state.isTableLoading}
          progressComponent={<Loader/>}
          persistTableHead
          highlightOnHover
          responsive
          onRowClicked={handleRowClick}
          selectableRowSelected={isSelectableRowSelected}
          selectableRowsHighlight
          conditionalRowStyles={conditionalRowStyles}
          expandableRows={isAccountingAdmin}
          expandableRowsComponent={<AccountingTransactionDetails/>}
        />
      </div>

      <PaymentLinkModal
        payment={state.paymentToProcess}
        parentDispatch={dispatch}
        values={state.linkModalValues}
        showContractSelect={state.linkModalTarget.contract}
        showOFRSelect={state.linkModalTarget.ofr}
        open={state.isLinkModalOpen}
        onRequestClose={handleCloseLinkProcessingModal}
      />

      <PaymentUnlinkModal
        payment={state.paymentToProcess}
        open={state.isUnlinkModalOpen}
        onRequestClose={handleCloseLinkProcessingModal}
      />

      <PaymentLinkLedgerModal
        isOpen={state.isLinkLedgerModalOpen}
        row={state.paymentToProcess}
        onRequestClose={handleCloseLedgerModal}
      />

      <PaymentUnlinkLedgerModal
        isOpen={state.isUnlinkLedgerModalOpen}
        row={state.paymentToProcess}
        onRequestClose={handleCloseLedgerModal}
      />

      <LinkChargeToTripModal
        isOpen={state.isLinkTripModalOpen}
        row={state.paymentToProcess}
        onRequestClose={handleCloseLinkTripModal}
      />

      <CompChargeModal
        isOpen={state.isCompChargeModalOpen}
        mode={state.compChargeModalMode}
        row={state.paymentToProcess}
        onRequestClose={handleCloseCompChargeModal}
      />
    </Fragment>
  );
};

export default AccountingDashboardContent;
